import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, TrendingUp, Activity, Calendar, Search, 
  ChevronRight, ArrowUpRight, ArrowDownRight, Minus,
  Ruler, Weight, Target, Award, Clock, FileText,
  BarChart3, PieChart, LineChart, RefreshCw, Loader2,
  UserPlus, Mail, Phone, CheckCircle, AlertCircle,
  Building2, Palette, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import { 
  LineChart as RechartsLine, Line, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart as RechartsPie, Pie, Cell, AreaChart, Area
} from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import PartnerBranding from './PartnerBranding';

const COLORS = ['#02b5ff', '#ff025f', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export default function PartnerDashboardContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalAthletes: 0,
    activeAthletes: 0,
    recentMeasurements: 0,
    completedTargets: 0,
    avgEngagement: 0
  });
  const [measurementTrends, setMeasurementTrends] = useState([]);
  const [engagementData, setEngagementData] = useState([]);
  const [ageDistribution, setAgeDistribution] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // Fetch athletes
      const athletesRes = await axios.get(`${API}/children`, { headers });
      const athleteData = athletesRes.data || [];
      setAthletes(athleteData);
      
      // Fetch referrals
      const referralsRes = await axios.get(`${API}/referrals`, { headers });
      setReferrals(referralsRes.data.referrals || []);
      
      // Fetch referral stats
      const statsRes = await axios.get(`${API}/referrals/stats`, { headers });
      setReferralStats(statsRes.data);
      
      // Calculate stats
      const now = new Date();
      const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
      
      let recentMeasurements = 0;
      let totalTargetsCompleted = 0;
      let totalEngagementScore = 0;
      let athletesWithActivity = 0;
      
      // Process each athlete for stats
      for (const athlete of athleteData) {
        // Count recent measurements
        if (athlete.last_measurement_date) {
          const lastMeasurement = new Date(athlete.last_measurement_date);
          if (lastMeasurement > thirtyDaysAgo) {
            recentMeasurements++;
          }
        }
        
        // Get engagement score (sessions logged, targets set, etc.)
        if (athlete.gamification?.total_points > 0) {
          totalEngagementScore += Math.min(100, athlete.gamification.total_points / 10);
          athletesWithActivity++;
        }
      }
      
      // Calculate age distribution
      const ageGroups = { 'Under 12': 0, '12-14': 0, '15-17': 0, '18-21': 0, 'Over 21': 0 };
      athleteData.forEach(athlete => {
        if (athlete.date_of_birth) {
          const age = Math.floor((now - new Date(athlete.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000));
          if (age < 12) ageGroups['Under 12']++;
          else if (age < 15) ageGroups['12-14']++;
          else if (age < 18) ageGroups['15-17']++;
          else if (age <= 21) ageGroups['18-21']++;
          else ageGroups['Over 21']++;
        }
      });
      
      setAgeDistribution(Object.entries(ageGroups).map(([name, value]) => ({ name, value })));
      
      // Set computed stats
      setStats({
        totalAthletes: athleteData.length,
        activeAthletes: athleteData.filter(a => a.status === 'active' || !a.status).length,
        recentMeasurements,
        completedTargets: totalTargetsCompleted,
        avgEngagement: athletesWithActivity > 0 ? Math.round(totalEngagementScore / athletesWithActivity) : 0
      });
      
      // Generate measurement trend data (mock for demo - would come from aggregated data)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
      setMeasurementTrends(months.map((month, i) => ({
        month,
        measurements: Math.floor(Math.random() * 20) + 10 + i * 2,
        athleticTests: Math.floor(Math.random() * 10) + 5
      })));
      
      // Generate engagement data
      setEngagementData([
        { name: 'Sessions Logged', value: athleteData.reduce((sum, a) => sum + (a.session_count || 0), 0) || 45 },
        { name: 'Targets Set', value: athleteData.reduce((sum, a) => sum + (a.target_count || 0), 0) || 32 },
        { name: 'Badges Earned', value: athleteData.reduce((sum, a) => sum + (a.badge_count || 0), 0) || 18 },
        { name: 'Reports Generated', value: Math.floor(athleteData.length * 0.7) }
      ]);
      
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredAthletes = athletes.filter(athlete => {
    const name = `${athlete.first_name} ${athlete.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const updateReferralStatus = async (referralId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/referrals/${referralId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Referral marked as ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update referral status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
            <Building2 className="h-6 w-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Partner Dashboard</h1>
            <p className="text-sm text-zinc-500">{user?.organization_name || 'Your Organization'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-zinc-700"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Button
            onClick={() => window.open(`/refer/${user?.brand_slug || ''}`, '_blank')}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Referral Link
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2 overflow-x-auto">
        {['overview', 'athletes', 'referrals', 'branding'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab 
                ? 'bg-indigo-500/20 text-indigo-300' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Athletes"
              value={stats.totalAthletes}
              icon={Users}
              color="indigo"
            />
            <StatCard
              title="Referrals (30d)"
              value={referralStats.recent_30_days || 0}
              icon={UserPlus}
              color="green"
              subtitle={`${referralStats.pending || 0} pending`}
            />
            <StatCard
              title="Recent Measurements"
              value={stats.recentMeasurements}
              icon={Ruler}
              color="blue"
              subtitle="Last 30 days"
            />
            <StatCard
              title="Avg. Engagement"
              value={`${stats.avgEngagement}%`}
              icon={Activity}
              color="purple"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Measurement Trends */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <LineChart className="h-5 w-5 text-indigo-400" />
                Measurement Activity
              </h3>
              <div style={{ height: 250, minHeight: 250 }}>
                <SafeResponsiveContainer width="100%" height="100%">
                  <AreaChart data={measurementTrends}>
                    <defs>
                      <linearGradient id="colorMeasurements" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#02b5ff" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#02b5ff" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="month" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="measurements" 
                      stroke="#02b5ff" 
                      fill="url(#colorMeasurements)"
                      name="Measurements"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="athleticTests" 
                      stroke="#ff025f" 
                      fill="transparent"
                      name="Athletic Tests"
                    />
                  </AreaChart>
                </SafeResponsiveContainer>
              </div>
            </div>

            {/* Age Distribution */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <PieChart className="h-5 w-5 text-indigo-400" />
                Athlete Age Distribution
              </h3>
              <div style={{ height: 250, minHeight: 250 }}>
                <SafeResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={ageDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                    >
                      {ageDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                    />
                  </RechartsPie>
                </SafeResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Engagement Metrics */}
          <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-400" />
              Engagement Metrics
            </h3>
            <div style={{ height: 200, minHeight: 200 }}>
              <SafeResponsiveContainer width="100%" height="100%">
                <BarChart data={engagementData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" stroke="#666" />
                  <YAxis type="category" dataKey="name" stroke="#666" width={120} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                  />
                  <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </SafeResponsiveContainer>
            </div>
          </div>

          {/* Recent Referrals */}
          <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-indigo-400" />
                Recent Referrals
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTab('referrals')}
                className="text-indigo-400"
              >
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            
            {referrals.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">No referrals yet</p>
            ) : (
              <div className="space-y-3">
                {referrals.slice(0, 5).map(ref => (
                  <div 
                    key={ref.id}
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <span className="text-indigo-400 font-medium">
                          {ref.first_name?.[0]}{ref.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{ref.first_name} {ref.last_name}</p>
                        <p className="text-xs text-zinc-500">{ref.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        ref.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        ref.status === 'contacted' ? 'bg-blue-500/20 text-blue-400' :
                        ref.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {ref.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Athletes Tab */}
      {activeTab === 'athletes' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search athletes..."
              className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* Athletes List */}
          {filteredAthletes.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No athletes found</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredAthletes.map(athlete => (
                <div 
                  key={athlete.id}
                  className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-indigo-500/50 transition-all cursor-pointer"
                  onClick={() => navigate(`/child/${athlete.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {athlete.first_name?.[0]}{athlete.last_name?.[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{athlete.first_name} {athlete.last_name}</h3>
                        <div className="flex items-center gap-3 text-sm text-zinc-400">
                          {athlete.current_team_name && (
                            <span>{athlete.current_team_name}</span>
                          )}
                          {athlete.date_of_birth && (
                            <span>
                              {Math.floor((new Date() - new Date(athlete.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))} years
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {athlete.last_measurement_date && (
                        <div className="text-right">
                          <p className="text-xs text-zinc-500">Last Measurement</p>
                          <p className="text-sm text-zinc-300">
                            {new Date(athlete.last_measurement_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      <ChevronRight className="h-5 w-5 text-zinc-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && (
        <div className="space-y-6">
          {/* Referral Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
              <p className="text-sm text-zinc-400">Total</p>
              <p className="text-2xl font-bold text-white">{referralStats.total || 0}</p>
            </div>
            <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
              <p className="text-sm text-yellow-400">Pending</p>
              <p className="text-2xl font-bold text-yellow-400">{referralStats.pending || 0}</p>
            </div>
            <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
              <p className="text-sm text-blue-400">Contacted</p>
              <p className="text-2xl font-bold text-blue-400">{referralStats.contacted || 0}</p>
            </div>
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
              <p className="text-sm text-green-400">Converted</p>
              <p className="text-2xl font-bold text-green-400">{referralStats.converted || 0}</p>
            </div>
          </div>

          {/* Referrals List */}
          {referrals.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No referrals yet</p>
              <p className="text-sm mt-2">Share your referral link to start receiving referrals</p>
            </div>
          ) : (
            <div className="space-y-4">
              {referrals.map(ref => (
                <div 
                  key={ref.id}
                  className="bg-zinc-800/50 rounded-xl p-5 border border-zinc-700/50"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white">{ref.first_name} {ref.last_name}</h3>
                        <span className={`px-2 py-1 rounded text-xs ${
                          ref.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          ref.status === 'contacted' ? 'bg-blue-500/20 text-blue-400' :
                          ref.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {ref.status}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Mail className="h-4 w-4" />
                          <span className="truncate">{ref.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Phone className="h-4 w-4" />
                          <span>{ref.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Calendar className="h-4 w-4" />
                          <span>{ref.date_of_birth}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Clock className="h-4 w-4" />
                          <span>{new Date(ref.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      {ref.reason_for_referral && (
                        <div className="mt-3 p-3 bg-zinc-900/50 rounded-lg">
                          <p className="text-xs text-zinc-500 mb-1">Reason for Referral</p>
                          <p className="text-sm text-zinc-300">{ref.reason_for_referral}</p>
                        </div>
                      )}
                      
                      {ref.referrer_name && (
                        <p className="text-xs text-zinc-500 mt-2">
                          Referred by: {ref.referrer_name} {ref.referrer_organization ? `(${ref.referrer_organization})` : ''}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {ref.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => updateReferralStatus(ref.id, 'contacted')}
                          className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                        >
                          Mark Contacted
                        </Button>
                      )}
                      {ref.status === 'contacted' && (
                        <Button
                          size="sm"
                          onClick={() => updateReferralStatus(ref.id, 'converted')}
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          Mark Converted
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/child/${ref.child_id}`)}
                        className="border-zinc-700"
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <PartnerBranding />
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    indigo: 'from-indigo-500/20 to-indigo-500/5 text-indigo-400',
    green: 'from-green-500/20 to-green-500/5 text-green-400',
    blue: 'from-blue-500/20 to-blue-500/5 text-blue-400',
    purple: 'from-purple-500/20 to-purple-500/5 text-purple-400',
    pink: 'from-pink-500/20 to-pink-500/5 text-pink-400'
  };
  
  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-4 border border-zinc-800`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-zinc-400">{title}</p>
      {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
    </div>
  );
}
